import { useCallback, useEffect, useState } from 'react';
import { Box, List, ListItem } from '@chakra-ui/react';
import { Button, Checkbox, Colors, Icon, Text } from '@mercator-gcp/grout';
import { produce } from 'immer';
import PopoverComponent from 'components/elements/Dropdown/PopoverComponent';
import { Spinner } from 'components/elements/Spinner';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import {
  markSelectionStates,
  getChildrenForLevel,
  flattenTreeToArray,
  getEmptyObject,
  filterLeafNodesByCategoriesAndStatus,
} from 'components/widgets/FilterToolbar/filters/LatestProjectStage/treeTraversalUtil';
import { useLatestProjectStageFilter } from 'hooks/filterToolbar/useLatestProjectStageFilter';
import { Group } from 'types';
import {
  DOCUMENT_TYPES,
  DEFAULT_ACCESSIBILITY_COMPLIANCE_SELECTION,
} from 'utils/constants/documentTypes';
import { formatPhaseLabelToString, PHASE_ORDER } from 'utils/constants/projectPhases';
import { PROJECT_STAGE_STATUS_ORDER } from 'utils/constants/projectStageCategories';
import { isEmptyObject } from 'utils/misc';
import { renameDocuments } from 'utils/renameDocuments';
import { DOCUMENT_STATUS_ORDER, sortByReference } from 'utils/sortByReference';
import type { DataMap, SelectionTreeNode, SelectionMap } from 'state/filterToolbar';

const listStyle = {
  paddingBottom: 'padding_8',
  height: '100%',
  overflowY: 'auto',
};

const clearAllContainer = {
  mt: 'padding_12',
  pl: 'padding_12',
  pr: 'padding_12',
};

const phaseText = {
  px: 'padding_12',
  py: 'padding_8',
};

const optionContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  paddingLeft: 'padding_12',
  paddingRight: 'padding_12',
  '&:hover': {
    backgroundColor: Colors.ArmyGreen100,
  },
};

const checkboxStyle = {
  width: '100%',
  paddingTop: 'padding_4',
  paddingBottom: 'padding_4',
};

const optionTextStyle = {
  fontSize: '13px',
  color: Colors.ArmyGreen900,
  width: '100%',
  whiteSpace: 'normal' as const,
  overflowWrap: 'anywhere' as const,
  textTransform: 'capitalize',
};

const clearAllText = {
  color: Colors.Tangerine900,
  cursor: 'pointer',
};

const arrowButtonStyle = {
  width: '16px',
  minWidth: '16px',
  height: '16px',
  background: 'none',
  color: 'inherit',
  border: 'none',
  padding: 0,
  font: 'inherit',
  outline: 'inherit',
  boxShadow: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    background: Colors.ArmyGreen200,
    borderRadius: '4px',
  },
};
const selectAllText = {
  pl: 'padding_8',
  pr: 'padding_8',
  pt: 'padding_4',
};

const ProjectStageOptions = ({
  dashboardTriggers,
  setSelectedOptions,
  selectedOptions,
  resetSelectedOptions,
  options,
  onChange,
  isLoading,
}: {
  dashboardTriggers: Group['triggers'];
  setSelectedOptions: (arg0: SelectionMap) => void;
  selectedOptions: SelectionMap;
  resetSelectedOptions: () => void;
  options: DataMap;
  onChange: (arg0: { id: number; categories: number[]; statuses: number[] }[]) => void;
  isLoading: boolean;
}) => {
  const { optionsGroupedByPhase } = useLatestProjectStageFilter({ options });
  // Track which popovers are open by their keyPath string
  const [openPopovers, setOpenPopovers] = useState<Set<string>>(new Set());

  useEffect(() => {
    /* On mount, check if the global filter state has selections that need to be preset.
     * This is redundant, but it keep the states aligned, and correctly syncs states on first load.
     */
    if (dashboardTriggers.length > 0) {
      const mapOfSelected = dashboardTriggers[0].documentTypes.reduce(
        (acc, trigger) => {
          acc[trigger.id] = {
            id: trigger.id,
            categories: trigger.categories,
            statuses: trigger.statuses,
          };

          return acc;
        },
        {} as { [key: number]: { id: number; categories: number[]; statuses: number[] } },
      );

      const newState = produce(options, (draft: DataMap) => {
        Object.keys(options).forEach((key) => {
          const selections = mapOfSelected[Number(key)];

          const result = filterLeafNodesByCategoriesAndStatus(
            options[key].children || {},
            selections?.categories || [],
            selections?.statuses || [],
          );

          draft[key].children = result;
        });

        return draft;
      });

      const markedState = markSelectionStates(options, newState);

      setSelectedOptions(markedState);
    } else if (isEmptyObject(selectedOptions)) {
      // If no triggers and no selections, ensure state is empty object
      setSelectedOptions(getEmptyObject(options));
    }
  }, [dashboardTriggers, options, isEmptyObject(selectedOptions)]);

  const handleClearAll = useCallback(() => {
    resetSelectedOptions();
    onChange([]);
  }, [resetSelectedOptions, onChange]);

  const propagateToggleToChildren = ({
    keyPath,
    forceSelectAll,
    forceClearAll,
  }: {
    keyPath: (string | number)[];
    forceSelectAll?: boolean;
    forceClearAll?: boolean;
  }) => {
    const currentLevel = getChildrenForLevel(keyPath, options);
    const totalChildren = currentLevel?.children;

    if (!totalChildren) return {};

    const currentSelectedLevel = getChildrenForLevel(
      keyPath,
      selectedOptions,
    ) as SelectionTreeNode;

    if (!forceSelectAll && (forceClearAll || currentSelectedLevel?.isSelected)) {
      if (!totalChildren.children) {
        // if no children, then we are at bottom level, return empty object
        return {};
      }

      // Deselect all children
      return getEmptyObject(totalChildren);
    }

    // Select all children
    return totalChildren;
  };

  const handleSelectionChange = useCallback(
    ({
      keyPath,
      leaf,
      forceSelectAll,
      forceClearAll,
    }: {
      keyPath: (string | number)[];
      leaf?: SelectionTreeNode;
      forceSelectAll?: boolean;
      forceClearAll?: boolean;
    }) => {
      const currentLevel = getChildrenForLevel(keyPath, options);
      if (!currentLevel?.children && !!leaf) {
        /* If we are at a leaf node, we just toggle that node */
        const newState = produce(selectedOptions, (draft: SelectionMap) => {
          const parentLevel = getChildrenForLevel(keyPath.slice(0, -1), draft);
          if (parentLevel?.children?.[leaf.value]) {
            delete parentLevel.children[leaf.value!];
          } else if (parentLevel?.children) {
            parentLevel!.children[leaf.value!] = leaf;
          }
        });
        /**
         * After updating child, update parent selection state
         * This will recursively update selection states up the tree
         */
        const markedState = markSelectionStates(options, newState);

        // Whenever selectedOptions changes, flatten and notify parent
        const flattened = flattenTreeToArray(markedState);
        onChange(flattened);

        setSelectedOptions(markedState);
      } else {
        const newChildren = propagateToggleToChildren({
          keyPath,
          forceSelectAll,
          forceClearAll,
        });

        const newState = produce(selectedOptions, (draft: SelectionMap) => {
          const currentSelectedLevel = getChildrenForLevel(keyPath, draft);
          if (!currentSelectedLevel?.children) {
            return;
          }
          if (
            currentLevel?.label === DOCUMENT_TYPES.ACCESSIBILITY_COMPLIANCE &&
            !currentSelectedLevel.isPartialSelected &&
            !currentSelectedLevel.isSelected
          ) {
            /**
             * For Accessibility Compliance, only include allowed leaf nodes.
             * We have a preset of filters for AC.
             */
            currentSelectedLevel.children = filterLeafNodesByCategoriesAndStatus(
              newChildren,
              DEFAULT_ACCESSIBILITY_COMPLIANCE_SELECTION.categories,
              DEFAULT_ACCESSIBILITY_COMPLIANCE_SELECTION.statuses,
            );
          } else {
            currentSelectedLevel.children = newChildren;
          }
        });
        /**
         * After updating child, update parent selection state
         * This will recursively update selection states up the tree
         */
        const markedState = markSelectionStates(options, newState);

        // Whenever selectedOptions changes, flatten and notify parent
        const flattened = flattenTreeToArray(markedState);
        onChange(flattened);

        setSelectedOptions(markedState);
      }
    },
    [setSelectedOptions, selectedOptions],
  );

  const renderChildOptions = useCallback(
    /**
     * Rendering children.
     * We need to fetch the current level in options, so that we know which children to render.
     * We also need to know which children are selected, so that we can set the checkbox state. This comes from the selectedOptions state
     */
    (keyPath: (string | number)[], parentOption: SelectionTreeNode) => {
      const currentSelectedLevel = getChildrenForLevel(
        keyPath,
        selectedOptions,
      ) as SelectionTreeNode;
      if (!parentOption.children) return null;
      if (!currentSelectedLevel?.children) return null;

      const keyPathString = keyPath.join('-');

      // Sort by alphabetical by default
      let optionsToRender: SelectionTreeNode[] = Object.values(
        parentOption.children,
      ).sort((a, b) => a.label.localeCompare(b.label));

      if (Object.keys(DOCUMENT_STATUS_ORDER).includes(parentOption.label)) {
        /**
         * We have specific sorting for some document types.
         */
        const document_type = parentOption.label as DOCUMENT_TYPES;
        const sorted = sortByReference(
          optionsToRender,
          (item: SelectionTreeNode) => item.label,
          DOCUMENT_STATUS_ORDER[document_type],
        );
        optionsToRender = sorted as SelectionTreeNode[];
      }
      if (parentOption.label.includes('Statuses')) {
        /**
         * For statuses, sort categories in order of progression.
         */
        const sorted = sortByReference(
          optionsToRender,
          (item: SelectionTreeNode) => item.label,
          PROJECT_STAGE_STATUS_ORDER,
        );
        optionsToRender = sorted as SelectionTreeNode[];
      }

      const handleSelectAll = () => {
        handleSelectionChange({
          keyPath,
          forceSelectAll: true,
        });
      };

      const clearAllForLevel = () => {
        handleSelectionChange({
          keyPath,
          forceClearAll: true,
        });
      };

      const handlePopoverOpen = () => {
        setOpenPopovers((prev) => new Set(prev).add(keyPathString));
      };

      const handlePopoverClose = () => {
        setOpenPopovers((prev) => {
          const next = new Set(prev);
          next.delete(keyPathString);

          return next;
        });
      };

      if (isLoading) {
        return <Spinner />;
      }

      return (
        <PopoverComponent onOpen={handlePopoverOpen} onClose={handlePopoverClose}>
          <FlexColumn>
            <FlexRow sx={selectAllText}>
              <Button variant="unstyled" onClick={handleSelectAll}>
                <Text.Small color={Colors.TextQuiet}>Select All</Text.Small>
              </Button>
              <Button variant="unstyled" onClick={clearAllForLevel}>
                <Text.Small color={Colors.Tangerine900}>Clear all</Text.Small>
              </Button>
            </FlexRow>
            <List sx={listStyle}>
              {optionsToRender.map((child: SelectionTreeNode) => {
                const currentLevel = currentSelectedLevel.children![child.value];

                if (!child?.children) {
                  // If no children, we are at leaf node.
                  return (
                    // @ts-ignore - Complex union type inference issue
                    <ListItem
                      key={`latest-stage-suboption-${keyPath.join('-')}-${child.value}`}
                      sx={optionContainerStyle}
                    >
                      <Checkbox
                        value={child.value}
                        sx={checkboxStyle}
                        isChecked={!!currentLevel}
                        onToggle={() =>
                          handleSelectionChange({
                            keyPath: [...keyPath, child.value],
                            leaf: child,
                          })
                        }
                      >
                        <Text sx={optionTextStyle}>{child.label}</Text>
                      </Checkbox>
                    </ListItem>
                  );
                }

                const childKeyPathString = [...keyPath, child.value].join('-');
                const isChildOpen = openPopovers.has(childKeyPathString);

                return (
                  <ListItem
                    key={`latest-stage-suboption-${keyPath.join('-')}-${child.value}`}
                    sx={{
                      ...optionContainerStyle,
                      // Maintain hover state when child filter is open
                      ...(isChildOpen && {
                        backgroundColor: Colors.ArmyGreen100,
                      }),
                    }}
                  >
                    <Checkbox
                      value={child.value}
                      sx={checkboxStyle}
                      isPartialCheck={currentLevel?.isPartialSelected || false}
                      isChecked={
                        currentLevel?.isPartialSelected ||
                        currentLevel?.isSelected ||
                        false
                      }
                      onToggle={() =>
                        handleSelectionChange({
                          keyPath: [...keyPath, child.value],
                          leaf: child,
                        })
                      }
                    >
                      <Text sx={optionTextStyle}>{child.label}</Text>
                    </Checkbox>
                    {child.children && (
                      <Box
                        sx={{
                          marginLeft: '16px',
                          // Maintain hover state on chevron when child is open
                          ...(isChildOpen && {
                            background: Colors.ArmyGreen200,
                            borderRadius: '4px',
                          }),
                        }}
                      >
                        {renderChildOptions([...keyPath, child.value], child)}
                      </Box>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </FlexColumn>
        </PopoverComponent>
      );
    },
    [selectedOptions, propagateToggleToChildren, handleSelectionChange, openPopovers],
  );

  return (
    <Box>
      {Object.keys(PHASE_ORDER).map((phase) => {
        const phaseOptions = optionsGroupedByPhase[phase];
        if (!phaseOptions || phaseOptions.length === 0) {
          return null;
        }

        return (
          <Box key={phase}>
            <Box sx={phaseText}>
              <Text.SmallLabel>{formatPhaseLabelToString(phase)}</Text.SmallLabel>
            </Box>
            <List>
              {(phaseOptions as SelectionTreeNode[]).map((option) => {
                const keyPathString = String(option.value);
                const isChildOpen = openPopovers.has(keyPathString);

                return (
                  <ListItem
                    sx={{
                      ...optionContainerStyle,
                      // Maintain hover state when child filter is open
                      ...(isChildOpen && {
                        backgroundColor: Colors.ArmyGreen100,
                      }),
                    }}
                    key={`latest-stage-option-${option.value}`}
                  >
                    <Checkbox
                      value={option.value}
                      sx={checkboxStyle}
                      onToggle={() => handleSelectionChange({ keyPath: [option.value] })}
                      isPartialCheck={
                        selectedOptions[option.value]?.isPartialSelected || false
                      }
                      isChecked={
                        selectedOptions[option.value]?.isPartialSelected ||
                        selectedOptions[option.value]?.isSelected ||
                        false
                      }
                    >
                      <Text sx={optionTextStyle}>{renameDocuments(option.label)}</Text>
                    </Checkbox>
                    {option.children && (
                      <Box
                        sx={{
                          ...arrowButtonStyle,
                          // Maintain hover state on chevron when child is open
                          ...(isChildOpen && {
                            background: Colors.ArmyGreen200,
                          }),
                        }}
                      >
                        <Icon name={Icon.Name.ChevronRight} fill={Colors.ArmyGreen500} />
                        {renderChildOptions([option.value], option)}
                      </Box>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        );
      })}

      <FlexRow sx={clearAllContainer}>
        <Box onClick={handleClearAll}>
          <Text.Small sx={clearAllText}>Clear all</Text.Small>
        </Box>
      </FlexRow>
    </Box>
  );
};

export default ProjectStageOptions;
